import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FadeIn } from '@/components/ui/motion';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import freeTrialBg from '@/assets/free-trial-bg.png';

export function FreeTrialSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Lazy load video when section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/plans');
    }
  };

  return (
    <section ref={sectionRef} className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 relative overflow-hidden -mt-1">
      {/* Fallback Background Image */}
      <img 
        src={freeTrialBg} 
        alt="" 
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
      />
      
      {/* Video Background - Lazy loaded */}
      {shouldLoadVideo && (
        <video
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={() => setVideoLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        >
          <source src="/videos/free-trial-bg.mp4" type="video/mp4" />
        </video>
      )}
      
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/30" />
      
      <FadeIn className="max-w-4xl mx-auto text-center relative z-10">
        <h2 className="text-white mb-6 md:mb-8 uppercase text-headline-1">
          Start Your<br />
          Journey Today
        </h2>
        
        {/* Inner curved section */}
        <div 
          className="px-3 sm:px-4 py-8 md:py-12 rounded-t-[35px] md:rounded-t-[140px]"
          style={{
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.10) 0%, rgba(0, 0, 0, 0.10) 100%)',
          }}
        >
          <p className="text-headline-3 text-white mb-4 md:mb-6 max-w-2xl mx-auto">
            Learn jazz the way it was meant to be—authentic, expressive, and rooted in tradition.
          </p>
          <div className="flex justify-center">
            <Button 
              onClick={handleGetStarted}
              size="cta"
              className="!bg-white !text-black hover:!bg-white/90 text-button"
            >
              VIEW PLANS
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </FadeIn>
    </section>
  );
}
